/**
 * Map context recovery.
 *
 * WebKit reclaims WebGL contexts under memory pressure. Mapbox then paints
 * nothing until `webglcontextrestored`, which on iOS routinely never comes,
 * and the map stays blank until the app restarts. A lost context that is not
 * restored within the grace period rebuilds the map: the caller keys its
 * `<Map>` on `generation` and seeds the rebuild with `resumeView`, the camera
 * at the moment the context went.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Map as MapboxMap } from "mapbox-gl";
import { analytics } from "@/services/analytics.service";

/** Give WebKit this long to restore a lost context before rebuilding the map. */
export const CONTEXT_RESTORE_GRACE_MS = 500;

export interface MapViewState {
  latitude: number;
  longitude: number;
  zoom: number;
}

export interface MapContextRecovery {
  /** Pass as the <Map> key; increments when a lost context has to be rebuilt. */
  generation: number;
  /** Camera at the moment the context was lost; null until a rebuild happens. */
  resumeView: MapViewState | null;
  /** Arm recovery on a loaded map. Safe to call more than once per instance. */
  watchMap: (map: MapboxMap) => void;
}

/** Live Mapbox canvases in the document - the count the memory ledger records. */
export const countLiveMapContexts = (): number =>
  typeof document === "undefined" ? 0 : document.querySelectorAll("canvas.mapboxgl-canvas").length;

export const useMapContextRecovery = (): MapContextRecovery => {
  const [rebuild, setRebuild] = useState<{ generation: number; view: MapViewState | null }>({
    generation: 0,
    view: null,
  });
  const watchedRef = useRef<MapboxMap | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const watchMap = useCallback((map: MapboxMap) => {
    if (watchedRef.current === map) return;
    watchedRef.current = map;

    map.on("webglcontextlost", () => {
      if (timerRef.current !== null) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        // Mapbox's own remove() loses the context too; a map that has left
        // the document is gone on purpose, not in need of a rebuild.
        if (!map.getCanvas().isConnected) return;
        const center = map.getCenter();
        const view = { latitude: center.lat, longitude: center.lng, zoom: map.getZoom() };
        analytics.capture("map_context_lost", {
          route: window.location.pathname,
          live_map_contexts: countLiveMapContexts(),
        });
        setRebuild(current => ({ generation: current.generation + 1, view }));
      }, CONTEXT_RESTORE_GRACE_MS);
    });

    map.on("webglcontextrestored", () => {
      if (timerRef.current === null) return;
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    });
  }, []);

  return { generation: rebuild.generation, resumeView: rebuild.view, watchMap };
};
